import api from './config';
import type {
  CreateGroupPayload,
  Group,
  GroupDetailResponse,
  GroupMembersResponse,
  GroupsListResponse,
  InvitePreview,
  JoinRequest,
  UpdateGroupPayload,
} from '@/types';
import { trackEvent } from '@/utils/analytics';

/** Circles the current user belongs to (Racore Official first). */
export const getMyGroups = async (page = 1, pageSize = 50): Promise<GroupsListResponse> => {
  const response = await api.get<GroupsListResponse>(
    `/groups/my-groups?pagination[page]=${page}&pagination[pageSize]=${pageSize}`,
  );
  return response.data;
};

/** Discover public circles, optionally filtered by name. */
export const discoverGroups = async (
  page = 1,
  pageSize = 25,
  nameFilter?: string,
): Promise<GroupsListResponse> => {
  const nameParam = nameFilter
    ? `&filters[name][$containsi]=${encodeURIComponent(nameFilter)}`
    : '';
  const response = await api.get<GroupsListResponse>(
    `/groups/discover?pagination[page]=${page}&pagination[pageSize]=${pageSize}${nameParam}`,
  );
  return response.data;
};

/** A single circle's detail (owner + the requester's membership). */
export const getGroupById = async (groupId: number): Promise<GroupDetailResponse> => {
  const response = await api.get<GroupDetailResponse>(`/groups/${groupId}`);
  return response.data;
};

export const createGroup = async (payload: CreateGroupPayload): Promise<{ data: Group }> => {
  const response = await api.post<{ data: Group }>('/groups', payload);
  trackEvent('Circle', 'create');
  return response.data;
};

export const updateGroup = async (
  groupId: number,
  payload: UpdateGroupPayload,
): Promise<{ data: Group }> => {
  const response = await api.put<{ data: Group }>(`/groups/${groupId}`, payload);
  return response.data;
};

/** Soft-delete a circle (admin only). */
export const deleteGroup = async (groupId: number): Promise<void> => {
  await api.delete(`/groups/${groupId}`);
};

/** Join a public circle by id. */
export const joinGroup = async (groupId: number): Promise<{ data: { success: boolean } }> => {
  const response = await api.post('/groups/join', { groupId });
  return response.data;
};

/** Leave a circle (only if allowed). */
export const leaveGroup = async (groupId: number): Promise<void> => {
  await api.delete(`/groups/${groupId}/members/me`);
};

/** Members of a circle (requester must be a member). */
export const getGroupMembers = async (groupId: number): Promise<GroupMembersResponse> => {
  const response = await api.get<GroupMembersResponse>(`/groups/${groupId}/members`);
  return response.data;
};

/** Remove a member from a circle (admin only). */
export const removeGroupMember = async (groupId: number, userId: number): Promise<void> => {
  await api.delete(`/groups/${groupId}/members/${userId}`);
};

/** Promote a member to admin (admin only; free circles cap at 3 admins). */
export const promoteGroupMember = async (groupId: number, userId: number): Promise<void> => {
  await api.post(`/groups/${groupId}/members/${userId}/promote`);
};

/** D10 — revoke a member's admin role (membership kept; BE enforces ≥1 admin). */
export const demoteGroupMember = async (groupId: number, userId: number): Promise<void> => {
  await api.post(`/groups/${groupId}/members/${userId}/demote`);
};

// ─── Invite links ────────────────────────────────────────────────────────────

/** Create (or reuse the active) invite code for a circle. Admin only. */
export const createInviteLink = async (groupId: number): Promise<string> => {
  const response = await api.post<{ data: { code: string } }>(`/groups/${groupId}/invite-links`);
  return response.data.data.code;
};

/** Deactivate an invite code. Admin only. */
export const deactivateInviteLink = async (groupId: number, code: string): Promise<void> => {
  await api.delete(`/groups/${groupId}/invite-links/${code}`);
};

/** Public preview of the circle behind an invite code (no auth required). */
export const previewInvite = async (code: string): Promise<InvitePreview> => {
  const response = await api.get<{ data: InvitePreview }>(`/invites/${code}`);
  return response.data.data;
};

/** Request to join the circle behind an invite code (creates a pending request). */
export const joinViaInviteCode = async (
  code: string,
): Promise<{ groupId: number; pending: boolean }> => {
  const response = await api.post<{ data: { groupId: number; pending?: boolean } }>(
    `/invites/${code}/join`,
  );
  const pending = !!response.data.data.pending;
  trackEvent('Circle', 'join-invite', pending ? 'pending' : 'joined');
  return { groupId: response.data.data.groupId, pending };
};

// ─── Join requests (admin approval) ───────────────────────────────────────────

export const getJoinRequests = async (groupId: number): Promise<JoinRequest[]> => {
  const response = await api.get<{ data: JoinRequest[] }>(`/groups/${groupId}/requests`);
  return response.data.data ?? [];
};

export const approveJoinRequest = async (groupId: number, userId: number): Promise<void> => {
  await api.post(`/groups/${groupId}/requests/${userId}/approve`);
};

export const rejectJoinRequest = async (groupId: number, userId: number): Promise<void> => {
  await api.delete(`/groups/${groupId}/requests/${userId}`);
};

// ─── A6 — per-group notification preferences ────────────────────────────────

export interface GroupNotifPrefs {
  notif_new_sound: boolean;
  notif_member_events: boolean;
  /** Replies on a reco shared in this circle. Separate from new sounds on
   *  purpose: a chatty thread must be silenceable without losing the sounds. */
  notif_replies: boolean;
}

/** The caller's own per-group notification prefs (defaults to all ON). */
export const getGroupNotifPrefs = async (groupId: number): Promise<GroupNotifPrefs> => {
  const response = await api.get<{ data: GroupNotifPrefs }>(
    `/groups/${groupId}/notification-preferences`,
  );
  return response.data.data;
};

/** Partial update of the caller's per-group prefs (booleans only). */
export const updateGroupNotifPrefs = async (
  groupId: number,
  update: Partial<GroupNotifPrefs>,
): Promise<GroupNotifPrefs> => {
  const response = await api.put<{ data: GroupNotifPrefs }>(
    `/groups/${groupId}/notification-preferences`,
    update,
  );
  return response.data.data;
};

// ─── A9 — unseen badge baseline ─────────────────────────────────────────────

/** The caller opened a circle: reset their unseen-badge baseline. */
export const markGroupSeen = async (groupId: number): Promise<void> => {
  await api.post(`/groups/${groupId}/seen`);
};
